import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPaperTradingFills, getPaperTradingState } from '../api/auth';
import { ConfirmDialog } from './ConfirmDialog';
import { useKeyboardSafeModal } from '../hooks/useKeyboardSafeModal';
import type { PaperTradingAccountSummary, PaperTradingFill, PaperTradingOrderItem } from '../types';
import type { ProviderName } from '../lib/providerRouting';

interface PaperTradingHistoryModalProps {
  open: boolean;
  currentSymbol: string;
  currentProvider: ProviderName;
  account: PaperTradingAccountSummary | null;
  onClose: () => void;
  onReset: () => Promise<void>;
}

type TradeHistoryTab = 'current' | 'all';
type TradeHistoryStatus = 'working' | 'filled' | 'cancelled';

const PAPER_TRADING_POLL_MS = 2_500;

interface TradeHistoryFilters {
  buy: boolean;
  sell: boolean;
  working: boolean;
  filled: boolean;
  cancelled: boolean;
}

interface TradeHistoryActivityItem {
  id: string;
  provider: ProviderName;
  symbol: string;
  side: 'buy' | 'sell';
  quantity: number;
  price: number;
  status: TradeHistoryStatus;
  orderType: PaperTradingOrderItem['orderType'] | 'fill';
  occurredAt: string;
}

const DEFAULT_FILTERS: TradeHistoryFilters = {
  buy: true,
  sell: true,
  working: true,
  filled: true,
  cancelled: true,
};

const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

const QUANTITY_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 6,
});

function buildActivityItems(orders: PaperTradingOrderItem[], fills: PaperTradingFill[]): TradeHistoryActivityItem[] {
  const orderItems = orders
    .filter((order) => order.status !== 'filled')
    .map((order) => ({
      id: `order-${order.id}`,
      provider: order.provider,
      symbol: order.symbol,
      side: order.side,
      quantity: order.quantity,
      price: order.price,
      status: order.status,
      orderType: order.orderType,
      occurredAt: order.createdAt,
    }));

  const fillItems = fills.map((fill) => ({
    id: `fill-${fill.id}`,
    provider: fill.provider,
    symbol: fill.symbol,
    side: fill.side,
    quantity: fill.quantity,
    price: fill.price,
    status: 'filled' as const,
    orderType: 'fill' as const,
    occurredAt: fill.occurredAt,
  }));

  return [...orderItems, ...fillItems].sort(
    (left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
  );
}

function formatPrice(value: number): string {
  return PRICE_FORMATTER.format(value);
}

function formatQuantity(value: number): string {
  return QUANTITY_FORMATTER.format(value);
}

function formatOccurredAt(value: string): string {
  return new Date(value).toLocaleString();
}

function formatMoney(value: number): string {
  return PRICE_FORMATTER.format(value);
}

function formatSignedMoney(value: number): string {
  const sign = value < 0 ? '-' : '+';
  return `${sign}${formatMoney(Math.abs(value))}`;
}

function getActivityTitle(item: TradeHistoryActivityItem): string {
  return `${item.side.toUpperCase()} ${formatQuantity(item.quantity)} ${item.symbol}`;
}

function getActivityDetail(item: TradeHistoryActivityItem): string {
  if (item.status === 'filled') {
    return `Filled @ ${formatPrice(item.price)} • ${formatOccurredAt(item.occurredAt)}`;
  }

  return `${item.orderType.toUpperCase()} @ ${formatPrice(item.price)} • ${item.status.toUpperCase()} • ${formatOccurredAt(item.occurredAt)}`;
}

function renderActivityCard(item: TradeHistoryActivityItem) {
  return (
    <div
      key={item.id}
      className="flex items-start justify-between gap-3 rounded-xl border border-border-subtle bg-surface-base/70 px-3 py-3"
    >
      <div className="min-w-0">
        <div className="font-medium text-slate-100">{getActivityTitle(item)}</div>
        <div className="text-xs text-slate-400">{getActivityDetail(item)}</div>
      </div>
      <span
        className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${
          item.side === 'buy' ? 'bg-emerald-500/15 text-emerald-200' : 'bg-rose-500/15 text-rose-200'
        }`}
      >
        {item.status}
      </span>
    </div>
  );
}

export function PaperTradingHistoryModal({
  open,
  currentSymbol,
  currentProvider,
  account,
  onClose,
  onReset,
}: PaperTradingHistoryModalProps) {
  const normalizedCurrentSymbol = currentSymbol.trim().toUpperCase();
  const [tab, setTab] = useState<TradeHistoryTab>('current');
  const [filters, setFilters] = useState<TradeHistoryFilters>(DEFAULT_FILTERS);
  const [orders, setOrders] = useState<PaperTradingOrderItem[]>([]);
  const [fills, setFills] = useState<PaperTradingFill[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { dialogAutoFocus, dialogRef, isKeyboardSafeLayout, viewportStyle } = useKeyboardSafeModal(open);

  const refreshActivity = useCallback(async () => {
    const [state, nextFills] = await Promise.all([getPaperTradingState(), getPaperTradingFills()]);
    setOrders(state.orders);
    setFills(nextFills);
    setErrorMessage(null);
  }, []);

  useEffect(() => {
    if (!open) {
      setTab('current');
      setFilters(DEFAULT_FILTERS);
      setOrders([]);
      setFills([]);
      setIsLoading(false);
      setIsResetConfirmOpen(false);
      setIsResetting(false);
      setSuccessMessage(null);
      setErrorMessage(null);
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    let isActive = true;

    const load = async () => {
      try {
        await refreshActivity();
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }
        setOrders([]);
        setFills([]);
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load paper trading history.');
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void load();

    const handleOnline = () => {
      void refreshActivity().catch((error: unknown) => {
        if (!isActive) {
          return;
        }
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load paper trading history.');
      });
    };

    const pollTimerId = window.setInterval(() => {
      if (!navigator.onLine) {
        return;
      }

      void refreshActivity().catch((error: unknown) => {
        if (!isActive) {
          return;
        }
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load paper trading history.');
      });
    }, PAPER_TRADING_POLL_MS);

    window.addEventListener('online', handleOnline);

    return () => {
      isActive = false;
      window.removeEventListener('online', handleOnline);
      window.clearInterval(pollTimerId);
    };
  }, [open, refreshActivity]);

  const activities = useMemo(() => buildActivityItems(orders, fills), [fills, orders]);

  const currentSymbolActivities = useMemo(
    () =>
      activities.filter(
        (item) => item.provider === currentProvider && item.symbol === normalizedCurrentSymbol,
      ),
    [activities, currentProvider, normalizedCurrentSymbol],
  );

  const tabActivities = tab === 'current' ? currentSymbolActivities : activities;

  const filteredActivities = useMemo(
    () =>
      tabActivities.filter((item) => {
        const sideAllowed = item.side === 'buy' ? filters.buy : filters.sell;
        const statusAllowed = filters[item.status];
        return sideAllowed && statusAllowed;
      }),
    [filters, tabActivities],
  );

  const visibleOrders = useMemo(
    () => filteredActivities.filter((item) => item.status !== 'filled'),
    [filteredActivities],
  );

  const visibleFills = useMemo(
    () => filteredActivities.filter((item) => item.status === 'filled'),
    [filteredActivities],
  );

  const handleConfirmReset = useCallback(async () => {
    setIsResetting(true);
    try {
      await onReset();
      await refreshActivity();
      setSuccessMessage('Paper trading account reset successfully.');
      setErrorMessage(null);
      setIsResetConfirmOpen(false);
    } catch (error: unknown) {
      setSuccessMessage(null);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to reset paper trading.');
    } finally {
      setIsResetting(false);
    }
  }, [onReset, refreshActivity]);

  if (!open) {
    return null;
  }

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex bg-slate-950/70 backdrop-blur-sm ${
          isKeyboardSafeLayout ? 'items-start justify-stretch p-3 sm:p-4' : 'items-center justify-center p-4'
        }`}
        style={viewportStyle}
        onClick={onClose}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Paper trading history"
          tabIndex={-1}
          className={`w-full border border-border-subtle bg-surface-elevated shadow-[0_26px_60px_rgba(2,8,24,0.68)] ring-1 ring-slate-100/5 ${
            isKeyboardSafeLayout
              ? 'flex max-h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl'
              : 'max-w-3xl rounded-2xl'
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Paper Trading</h2>
              <p className="text-xs text-slate-400">Review current-symbol and account-wide paper trading activity.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-slate-400 transition-colors hover:bg-surface-panel/70 hover:text-slate-100"
              aria-label="Close paper trading history"
            >
              X
            </button>
          </div>

          <div className="border-b border-border-subtle px-4 py-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="grid flex-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {account ? (
                  <>
                    <div className="rounded-xl border border-border-subtle bg-surface-base/70 px-3 py-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Starting</div>
                      <div className="mt-1 text-sm font-semibold text-slate-100">{formatMoney(account.startingBalance)} {account.currency}</div>
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-surface-base/70 px-3 py-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Cash</div>
                      <div className="mt-1 text-sm font-semibold text-slate-100">{formatMoney(account.cashBalance)} {account.currency}</div>
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-surface-base/70 px-3 py-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Equity</div>
                      <div className="mt-1 text-sm font-semibold text-slate-100">{formatMoney(account.equity)} {account.currency}</div>
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-surface-base/70 px-3 py-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Realized</div>
                      <div className={`mt-1 text-sm font-semibold ${account.realizedPnl >= 0 ? 'text-emerald-200' : 'text-rose-200'}`}>
                        {formatSignedMoney(account.realizedPnl)} {account.currency}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-border-subtle bg-surface-base/70 px-3 py-4 text-sm text-slate-400 sm:col-span-2 xl:col-span-4">
                    Paper trading stats are unavailable.
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsResetConfirmOpen(true)}
                disabled={isResetting}
                className="shrink-0 rounded-xl border border-rose-500/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-100 transition-colors hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isResetting ? 'Resetting...' : 'Reset Paper Trading'}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
            {[
              ['current', `Current (${currentSymbolActivities.length})`],
              ['all', `All (${activities.length})`],
            ].map(([value, label], index) => (
              <button
                key={value}
                type="button"
                autoFocus={index === 0 ? dialogAutoFocus : false}
                onClick={() => setTab(value as TradeHistoryTab)}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  tab === value ? 'bg-slate-100 text-slate-900' : 'bg-surface-base/80 text-slate-300 hover:bg-surface-panel/90'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="border-b border-border-subtle px-4 py-3">
            <div className="flex flex-wrap gap-2">
              {[
                ['buy', 'Buy'],
                ['sell', 'Sell'],
                ['working', 'Working'],
                ['filled', 'Filled'],
                ['cancelled', 'Cancelled'],
              ].map(([key, label]) => {
                const isActive = filters[key as keyof TradeHistoryFilters];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      setFilters((current) => ({
                        ...current,
                        [key]: !current[key as keyof TradeHistoryFilters],
                      }))
                    }
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      isActive
                        ? 'border-brand-primary/70 bg-brand-primary/20 text-slate-100'
                        : 'border-border-subtle bg-surface-base/60 text-slate-400 hover:border-brand-primary/45 hover:text-slate-100'
                    }`}
                    aria-pressed={isActive}
                  >
                    {isActive ? `Hide ${label}` : `Show ${label}`}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`overflow-y-auto p-4 ${isKeyboardSafeLayout ? 'min-h-0 flex-1' : 'max-h-[60vh]'}`}>
            {isLoading ? (
              <p className="rounded-lg border border-border-subtle bg-surface-base/50 px-3 py-4 text-sm text-slate-300">
                Loading paper trading activity...
              </p>
            ) : null}

            {errorMessage ? (
              <p className="rounded-lg border border-rose-400/45 bg-rose-500/10 px-3 py-4 text-sm text-rose-100">
                {errorMessage}
              </p>
            ) : null}

            {successMessage ? (
              <p className="rounded-lg border border-emerald-400/45 bg-emerald-500/10 px-3 py-4 text-sm text-emerald-100">
                {successMessage}
              </p>
            ) : null}

            {!isLoading && !errorMessage && filteredActivities.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border-subtle px-3 py-4 text-center text-sm text-slate-400">
                {tab === 'current'
                  ? `No paper trading activity matches the active filters for ${normalizedCurrentSymbol}.`
                  : 'No paper trading activity matches the active filters.'}
              </p>
            ) : null}

            {!isLoading && !errorMessage && filteredActivities.length > 0 ? (
              <div className="space-y-5">
                <section className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Orders</h3>
                    <span className="text-xs text-slate-500">{visibleOrders.length}</span>
                  </div>
                  {visibleOrders.length > 0 ? (
                    <div className="space-y-2">{visibleOrders.map((item) => renderActivityCard(item))}</div>
                  ) : (
                    <p className="rounded-lg border border-dashed border-border-subtle px-3 py-4 text-center text-sm text-slate-500">
                      No orders match the active filters.
                    </p>
                  )}
                </section>

                <section className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Fills</h3>
                    <span className="text-xs text-slate-500">{visibleFills.length}</span>
                  </div>
                  {visibleFills.length > 0 ? (
                    <div className="space-y-2">{visibleFills.map((item) => renderActivityCard(item))}</div>
                  ) : (
                    <p className="rounded-lg border border-dashed border-border-subtle px-3 py-4 text-center text-sm text-slate-500">
                      No fills match the active filters.
                    </p>
                  )}
                </section>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={isResetConfirmOpen}
        title="Reset paper trading"
        message="This clears all paper orders, fills, positions, and resets the account balance back to the starting amount."
        onClose={() => {
          if (!isResetting) {
            setIsResetConfirmOpen(false);
          }
        }}
        onConfirm={handleConfirmReset}
        confirmLabel="Yes"
        cancelLabel="No"
        confirmTone="danger"
        isConfirming={isResetting}
      />
    </>
  );
}
